// src/components/admin/CompanyForm.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  FormControlLabel,
  Switch,
  Avatar,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Web as WebIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

const CompanyForm = ({ initialData, onSave, onCancel }) => {
  // État du formulaire
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    description: initialData?.description || '',
    licenseNumber: initialData?.licenseNumber || '',
    website: initialData?.website || '',
    facebook: initialData?.socialMedia?.facebook || '',
    instagram: initialData?.socialMedia?.instagram || '',
    twitter: initialData?.socialMedia?.twitter || '',
    username: '',
    password: '',
    isActive: initialData ? initialData.isActive : true,
  });
  
  // État pour le logo
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(initialData?.logo || '');
  
  // État des erreurs
  const [errors, setErrors] = useState({});
  
  // Gérer les changements de champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Supprimer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Gérer le changement de statut
  const handleSwitchChange = (e) => {
    setFormData(prev => ({ ...prev, isActive: e.target.checked }));
  };
  
  // Gérer l'upload du logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };
  
  // Supprimer le logo
  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoPreview('');
  };
  
  // Validation de base
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Le nom est requis';
    if (!formData.email) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.phone) newErrors.phone = 'Le téléphone est requis';
    if (!formData.address) newErrors.address = 'L\'adresse est requise';
    if (!formData.username) newErrors.username = 'Le nom d\'utilisateur est requis';
    if (!initialData && !formData.password) newErrors.password = 'Le mot de passe est requis';
    else if (formData.password && formData.password.length < 6) newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Préparer les données à envoyer
      const companyData = {
        ...formData,
        socialMedia: {
          facebook: formData.facebook,
          instagram: formData.instagram,
          twitter: formData.twitter
        },
        logo: logo
      };
      
      // Supprimer les propriétés qui sont déjà dans socialMedia
      delete companyData.facebook;
      delete companyData.instagram;
      delete companyData.twitter;
      
      // Si c'est une mise à jour et qu'il n'y a pas de nouveau mot de passe
      if (initialData && !companyData.password) {
        delete companyData.password;
      }
      
      // Envoyer les données
      onSave(companyData);
    }
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Informations générales
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de la compagnie"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                name="address"
                value={formData.address}
                onChange={handleChange}
                error={Boolean(errors.address)}
                helperText={errors.address}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Numéro de licence"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Site web"
                name="website"
                value={formData.website}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          {/* Réseaux sociaux */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Réseaux sociaux
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Facebook"
                name="facebook"
                value={formData.facebook}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FacebookIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InstagramIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Twitter"
                name="twitter"
                value={formData.twitter}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TwitterIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          {/* Identifiants */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Identifiants de connexion
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom d'utilisateur"
                name="username"
                value={formData.username}
                onChange={handleChange}
                error={Boolean(errors.username)}
                helperText={errors.username}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={initialData ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="Compte actif"
              />
            </Grid>
          </Grid>
        </Grid>
        
        {/* Logo */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>
            Logo
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {logoPreview ? (
              <Avatar
                src={logoPreview}
                alt="Logo de la compagnie"
                sx={{ width: 150, height: 150, mb: 2 }}
              />
            ) : (
              <Avatar
                sx={{ width: 150, height: 150, mb: 2, bgcolor: 'primary.main' }}
              >
                {formData.name.charAt(0) || "C"}
              </Avatar>
            )}
            
            <input
              accept="image/*"
              id="logo-upload"
              type="file"
              hidden
              onChange={handleLogoChange}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<AddPhotoAlternateIcon />}
              >
                {logoPreview ? 'Changer le logo' : 'Ajouter un logo'}
              </Button>
            </label>
            
            {logoPreview && (
              <Button
                color="error"
                onClick={handleRemoveLogo}
                startIcon={<DeleteIcon />}
                sx={{ mt: 1 }}
              >
                Supprimer
              </Button>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Formats recommandés: JPG, PNG, GIF<br />
              Taille maximale: 5MB
            </Typography>
          </Box>
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ mr: 2 }}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
        >
          {initialData ? 'Mettre à jour' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyForm;